import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  NgZone,
  signal,
  TemplateRef,
  inject,
  output,
  ViewChild
} from '@angular/core';
import { Select, SelectChangeEvent, SelectStyle } from 'primeng/select';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule
} from '@angular/forms';
import { FilterService, PrimeTemplate } from 'primeng/api';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-dropdown',
  templateUrl: './m-dropdown.component.html',
  styleUrls: ['./m-dropdown.component.scss'],
  providers: [
    SelectStyle,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MDropdownComponent),
      multi: true
    }
  ],
  imports: [Select, FormsModule, PrimeTemplate, NgTemplateOutlet]
})
export class MDropdownComponent extends Select implements ControlValueAccessor {
  @ViewChild('dropDown') dropDown!: Select;

  constructor() {
    const zone = inject(NgZone);
    const filterService = inject(FilterService);

    super(zone, filterService);
  }

  @Input() width: string | undefined;
  @Input() height: string | undefined;
  @Input() preventSelection = false;
  @Input() customTemplate: TemplateRef<unknown> | undefined;
  // Distinct input names are required here: reusing the inherited PrimeNG Select
  // template names (selectedItemTemplate/groupTemplate/footerTemplate) lets the
  // inherited @ContentChild queries overwrite the @Input values with undefined
  // after content init, so the projected templates never reach the inner p-select.
  @Input() customSelectedTemplate: TemplateRef<unknown> | undefined;
  @Input() customGroupTemplate: TemplateRef<unknown> | undefined;
  @Input() customFooterTemplate: TemplateRef<unknown> | undefined;
  readonly formControlChange = output<SelectChangeEvent>();

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: unknown) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: () => void = () => {};

  // PrimeNG 20 applies the root host class + container-click on the host element via
  // `host: { '[class]': "cn(cx('root'), styleClass)", '(click)': 'onContainerClick()' }`.
  // This wrapper extends Select but renders its OWN inner <p-select>, so that host metadata is
  // inherited onto <m-dropdown> too — duplicating p-select styling and firing a second container
  // click that fights the inner overlay. Neutralize both on the wrapper host; the inner instance
  // is unaffected (`cx` is never called in PrimeNG's TS body, only in host/template bindings).
  override cx(): string {
    return '';
  }

  override onContainerClick(): void {
    // no-op: the inner <p-select> handles its own container click
  }

  override writeValue(value: unknown): void {
    this.value = value;
    this.cd.markForCheck();
  }

  override registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  override registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  // Local disabled state driven by the CVA (PrimeNG 20 `disabled` is a read-only signal input)
  protected disabledState = signal(false);

  override setDisabledState(isDisabled: boolean): void {
    this.disabledState.set(isDisabled);
  }

  handleValueChange(newValue: unknown): void {
    if (this.preventSelection) {
      this.writeValue(null);
      return;
    }
    this.value = newValue;
    this._onChange(newValue);
    this._onTouched();
    this.formControlChange.emit({
      value: newValue,
      originalEvent: null as unknown as Event
    });
  }

  closePanel(isFocus?: boolean): void {
    this.dropDown?.hide(isFocus);
  }
}
